import React, { useState } from 'react';
import { Button, Form, Modal } from 'react-bootstrap';
import { toast } from 'react-toastify';

import { Group } from '../../../domain/group';
import { CreateUserData } from '../../../repository/users/useCreateUserRepository';
import { usePermissions } from '../../../components/auth/usePermissions';
import { Permission } from '../../../domain/permissions';

type CreateUserModalProps = {
    show: boolean;
    onClose: () => void;
    onSave: (data: CreateUserData) => Promise<void>;
    allGroups: Group[];
}

export const CreateUserModal = ({ show, onClose, onSave, allGroups }: CreateUserModalProps) => {
    const { hasPermission } = usePermissions();
    const [formData, setFormData] = useState<Omit<CreateUserData, 'groupIds'>>({
        firstName: '',
        lastname: '',
        email: '',
        phoneNumber: '',
        preferredOrderIdSuffix: '',
    });
    const [selectedGroupIds, setSelectedGroupIds] = useState<string[]>([]);
    const [isSaving, setIsSaving] = useState(false);

    const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const { name, value } = e.target;
        setFormData(prev => ({ ...prev, [name]: value }));
    };

    const handleGroupToggle = (groupId: string) => {
        if (selectedGroupIds.includes(groupId)) {
            setSelectedGroupIds(selectedGroupIds.filter(id => id !== groupId));
        } else {
            setSelectedGroupIds([...selectedGroupIds, groupId]);
        }
    };

    const handleSave = async (e: React.FormEvent) => {
        e.preventDefault();
        setIsSaving(true);
        try {
            await onSave({ ...formData, groupIds: selectedGroupIds });
            setFormData({
                firstName: '',
                lastname: '',
                email: '',
                phoneNumber: '',
                preferredOrderIdSuffix: '',
            });
            setSelectedGroupIds([]);
            onClose();
        } catch (error) {
            toast.error('Nie udało się dodać użytkownika');
            console.error(error);
        } finally {
            setIsSaving(false);
        }
    };

    return (
        <Modal show={show} onHide={onClose}>
            <Modal.Header closeButton>
                <Modal.Title>Dodaj nowego użytkownika</Modal.Title>
            </Modal.Header>
            <Form onSubmit={handleSave}>
                <Modal.Body>
                    <Form.Group className="mb-3">
                        <Form.Label>Imię</Form.Label>
                        <Form.Control
                            name="firstName"
                            value={formData.firstName}
                            onChange={handleInputChange}
                            required
                        />
                    </Form.Group>
                    <Form.Group className="mb-3">
                        <Form.Label>Nazwisko</Form.Label>
                        <Form.Control
                            name="lastname"
                            value={formData.lastname}
                            onChange={handleInputChange}
                            required
                        />
                    </Form.Group>
                    <Form.Group className="mb-3">
                        <Form.Label>Email</Form.Label>
                        <Form.Control
                            type="email"
                            name="email"
                            value={formData.email}
                            onChange={handleInputChange}
                            required
                        />
                    </Form.Group>
                    <Form.Group className="mb-3">
                        <Form.Label>Numer telefonu</Form.Label>
                        <Form.Control
                            name="phoneNumber"
                            value={formData.phoneNumber}
                            onChange={handleInputChange}
                            placeholder="+48123456789"
                            required
                        />
                    </Form.Group>
                    <Form.Group className="mb-3">
                        <Form.Label>Preferowany sufiks numeru zlecenia</Form.Label>
                        <Form.Control
                            name="preferredOrderIdSuffix"
                            value={formData.preferredOrderIdSuffix}
                            onChange={handleInputChange}
                            placeholder="np. SN"
                        />
                    </Form.Group>
                    <Form.Group className="mb-3">
                        <Form.Label>Wybierz grupy</Form.Label>
                        {allGroups.map((group) => {
                            const isAdministrative = group.permissions.includes(Permission.ADMIN_MANAGE_USERS) ||
                                group.permissions.includes(Permission.ADMIN_MANAGE_GROUPS) ||
                                group.permissions.includes(Permission.OWNER_ASSIGN_ADMIN);
                            const isDisabled = isAdministrative && !hasPermission(Permission.OWNER_ASSIGN_ADMIN);

                            return (
                                <Form.Check
                                    key={group.id}
                                    type="checkbox"
                                    id={`new-user-group-${group.id}`}
                                    label={group.name}
                                    checked={selectedGroupIds.includes(group.id)}
                                    onChange={() => handleGroupToggle(group.id)}
                                    disabled={isDisabled}
                                />
                            );
                        })}
                    </Form.Group>
                </Modal.Body>
                <Modal.Footer>
                    <Button variant="secondary" onClick={onClose}>Anuluj</Button>
                    <Button variant="primary" type="submit" disabled={isSaving}>
                        {isSaving ? 'Zapisywanie...' : 'Dodaj użytkownika'}
                    </Button>
                </Modal.Footer>
            </Form>
        </Modal>
    );
};
